import { useLocation, useNavigate } from 'react-router-dom';
import type { FC, ReactNode } from 'react';
import { SiteThemeToggle } from '../SiteThemeToggle';
import styles from './Header.module.scss';

export interface HeaderProps {
  title?: string;
  pageTitle?: string; // Title of the current page, centred in the header
  subtitle?: string;
  navItems?: string[];
  className?: string;
  onNavItemClick?: (item: string) => void;
  logoIcon?: ReactNode;
}

// Route owned by each nav label; used to mark the active item and as the
// fallback target when a page does not supply its own handler.
const NAV_ROUTES: Record<string, string> = {
  'Model Exploration': '/model-exploration',
  Tools: '/tools',
  Learning: '/learning',
  Contact: '/contact',
};

const Header: FC<HeaderProps> = ({
  title = 'Cerebellum Facility Hub',
  pageTitle,
  subtitle = 'DBBS Lab · University of Pavia',
  navItems = ['Model Exploration', 'Tools', 'Learning', 'Contact'],
  className = '',
  onNavItemClick,
  logoIcon,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleNavItemClick = (item: string) => {
    if (onNavItemClick) {
      onNavItemClick(item);
    } else if (NAV_ROUTES[item]) {
      navigate(NAV_ROUTES[item]);
    }
  };

  return (
    <header className={`${styles.header} ${className}`}>
      <a
        href="/"
        className={styles.header__brand}
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
      >
        <span className={styles.header__mark}>
          {logoIcon || <img src="/images/logo_dbbs.svg" alt="" />}
        </span>
        <span className={styles.header__wordmark}>
          <span className={styles.header__title}>{title}</span>
          {subtitle && <span className={styles.header__subtitle}>{subtitle}</span>}
        </span>
      </a>

      {pageTitle && <span className={styles.header__page}>{pageTitle}</span>}

      <nav className={styles.header__nav} aria-label="Primary">
        {navItems.map((item) => {
          const route = NAV_ROUTES[item];
          const active = route !== undefined && pathname.startsWith(route);
          return (
            <button
              key={item}
              type="button"
              className={styles.header__item}
              aria-current={active ? 'page' : undefined}
              onClick={() => handleNavItemClick(item)}
            >
              {item}
            </button>
          );
        })}
        <span className={styles.header__trailing}>
          <SiteThemeToggle color="inherit" size="medium" />
        </span>
      </nav>
    </header>
  );
};

export default Header;
